import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';
import { platformFetch } from '../platform/http';

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'success' | 'pending' | 'error'>('pending');
  const [credits, setCredits] = useState(0);
  const [message, setMessage] = useState('');
  const [checkCount, setCheckCount] = useState(0);

  const refreshUserInfo = useCallback(async () => {
    try {
      const userInfo = await api.getMe();
      // 通知其他页面积分已更新
      window.dispatchEvent(new CustomEvent('credits-updated', { detail: { credits: userInfo.credits } }));
    } catch (err) {
      console.error('刷新用户信息失败:', err);
    }
  }, []);

  const checkPaymentStatus = useCallback(async () => {
    const outTradeNo = searchParams.get('out_trade_no');
    if (!outTradeNo) {
      setStatus('error');
      setMessage('缺少订单号');
      return;
    }

    try {
      // 统一走平台请求层（修复此前硬编码 /api 前缀、API base 非默认环境打错地址的问题）
      const response = await platformFetch(`/payment/order/${outTradeNo}`);

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'paid') {
          setStatus('success');
          setCredits(data.credits || 0);
          await refreshUserInfo();
          return true;
        } else if (data.status === 'failed') {
          setStatus('error');
          setMessage('支付失败，请重新尝试');
          return true;
        }
      }
    } catch (err) {
      console.error('查询订单状态失败:', err);
    }
    return false;
  }, [searchParams, refreshUserInfo]);

  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/payment/success')) {
      setStatus('success');
      setCredits(Number(searchParams.get('credits')) || 0);
      refreshUserInfo();
      return;
    } else if (path.includes('/payment/error')) {
      setStatus('error');
      setMessage(searchParams.get('msg') || '支付处理失败');
      return;
    }

    // 自动轮询查询订单状态
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = async () => {
      // 立即检查一次
      const found = await checkPaymentStatus();
      if (found) return;

      // 每 3 秒检查一次，最多 20 次（60 秒）
      let count = 0;
      const id = setInterval(async () => {
        count++;
        setCheckCount(count);
        const found = await checkPaymentStatus();
        if (found || count >= 20) {
          clearInterval(id);
          if (!found) {
            setStatus('error');
            setMessage('支付状态查询超时，请稍后到用户中心查看');
          }
        }
      }, 3000);
      intervalId = id;
    };

    startPolling();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [searchParams, checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">支付成功</h1>
            <p className="text-gray-600 mb-2">感谢您的充值！</p>
            {credits > 0 && (
              <p className="text-green-600 font-semibold mb-6">
                已获得 {credits} 积分
              </p>
            )}
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">支付处理中</h1>
            <p className="text-gray-600 mb-2">
              系统正在确认支付结果，请稍候...
            </p>
            {checkCount > 0 && (
              <p className="text-gray-400 text-sm mb-6">
                已查询 {checkCount} 次...
              </p>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">支付失败</h1>
            <p className="text-red-600 mb-6">{message}</p>
          </>
        )}

        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          返回首页
        </button>
      </div>
    </div>
  );
}
